import {
  addDays as dateFnsAddDays,
  addMonths as dateFnsAddMonths,
  format,
  getDay,
  getDaysInMonth,
  isEqual,
  parseISO,
  startOfMonth,
  startOfWeek,
} from 'date-fns';

import type { CalendarEvent, Conflict, Hour, ISODate, Member } from './types';

// ---------- Date math ----------

export function parseISODate(date: ISODate): Date {
  return parseISO(date);
}

export function formatISODate(date: Date): ISODate {
  return format(date, 'yyyy-MM-dd');
}

export function addDays(date: ISODate, n: number): ISODate {
  return formatISODate(dateFnsAddDays(parseISO(date), n));
}

export function addMonths(date: ISODate, n: number): ISODate {
  return formatISODate(dateFnsAddMonths(parseISO(date), n));
}

export function dateEquals(a: ISODate, b: ISODate): boolean {
  return isEqual(parseISO(a), parseISO(b));
}

export function dateCompare(a: ISODate, b: ISODate): number {
  const da = parseISO(a);
  const db = parseISO(b);
  if (isEqual(da, db)) return 0;
  return da < db ? -1 : 1;
}

/** 0=Sun, 1=Mon, ..., 6=Sat */
export function dayOfWeek(date: ISODate): number {
  return getDay(parseISO(date));
}

/** Returns the Monday (or Sunday) of the week containing `date`. */
export function mondayOf(date: ISODate, weekStartsOn: 0 | 1 = 1): ISODate {
  return formatISODate(
    startOfWeek(parseISO(date), { weekStartsOn }),
  );
}

/** Returns the first day of the month containing `date`. */
export function firstOfMonth(date: ISODate): ISODate {
  return formatISODate(startOfMonth(parseISO(date)));
}

export function daysInMonth(date: ISODate): number {
  return getDaysInMonth(parseISO(date));
}

export function todayISO(): ISODate {
  return formatISODate(new Date());
}

// ---------- Time math ----------

/**
 * Formats a decimal hour as a human-readable time string.
 * 9 → "9 AM", 13.5 → "1:30 PM", 12 → "12 PM"
 */
export function formatTime(hour: Hour): string {
  const totalMinutes = Math.round(hour * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  const period = h < 12 ? 'AM' : 'PM';
  const displayH = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return m === 0 ? `${displayH} ${period}` : `${displayH}:${String(m).padStart(2, '0')} ${period}`;
}

/**
 * Compact tile format: 9 → "9am", 13.5 → "1:30pm", 12 → "12pm"
 */
export function formatTimeTile(hour: Hour): string {
  const totalMinutes = Math.round(hour * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  const period = h < 12 ? 'am' : 'pm';
  const displayH = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return m === 0 ? `${displayH}${period}` : `${displayH}:${String(m).padStart(2, '0')}${period}`;
}

/**
 * Rounds a decimal hour to the nearest snap interval.
 * snapMinutes=15: 9.1 → 9.0, 9.15 → 9.25
 */
export function roundToSnap(hour: Hour, snapMinutes: 15 | 30 | 60 = 15): Hour {
  const snapHours = snapMinutes / 60;
  return Math.round(hour / snapHours) * snapHours;
}

/**
 * Converts a pixel offset (dragY) within a day column into a snapped Hour.
 * baseHour: the first displayed hour (e.g. 8)
 * offsetY: pixels from the top of the time grid (not the header)
 * slotHeight: pixels per hour
 */
export function snapHourFromOffset(
  baseHour: Hour,
  offsetY: number,
  slotHeight: number,
  snapMinutes: 15 | 30 | 60 = 15,
): Hour {
  const rawHour = baseHour + offsetY / slotHeight;
  return roundToSnap(rawHour, snapMinutes);
}

// ---------- Period labels ----------

export function computePeriodLabel(
  cursor: ISODate,
  view: 'week' | 'month' | 'schedule',
  weekStartsOn: 0 | 1 = 1,
): string {
  const d = parseISO(cursor);

  if (view === 'month') {
    return format(d, 'MMMM yyyy');
  }

  if (view === 'schedule') {
    return format(d, 'MMMM yyyy');
  }

  // week
  const weekStart = startOfWeek(d, { weekStartsOn });
  const weekEnd = dateFnsAddDays(weekStart, 6);

  if (weekStart.getFullYear() !== weekEnd.getFullYear()) {
    return `${format(weekStart, 'MMMM d, yyyy')} – ${format(weekEnd, 'MMMM d, yyyy')}`;
  }
  if (weekStart.getMonth() !== weekEnd.getMonth()) {
    return `${format(weekStart, 'MMMM d')} – ${format(weekEnd, 'MMMM d, yyyy')}`;
  }
  return `${format(weekStart, 'MMMM d')} – ${format(weekEnd, 'd, yyyy')}`;
}

// ---------- Color helpers ----------

/**
 * Creates an opaque pastel tint from a hex color by blending toward white at 18% opacity.
 * Using opaque rather than alpha-blended so hatched backgrounds don't bleed through.
 */
export function tint(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const alpha = 0.18;
  return `rgb(${Math.round(r * alpha + 255 * (1 - alpha))},${Math.round(g * alpha + 255 * (1 - alpha))},${Math.round(b * alpha + 255 * (1 - alpha))})`;
}

/**
 * Darkens a hex color by multiplying each channel by 0.45 for use as text on the tinted background.
 */
export function darken(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgb(${Math.round(r * 0.45)},${Math.round(g * 0.45)},${Math.round(b * 0.45)})`;
}

// ---------- Conflict detection ----------

/**
 * Returns the first event that overlaps [start, end) on `date`,
 * excluding the event with `ignoreId`.
 *
 * Blocks and overrides are always checked. Sessions are only flagged when
 * `date >= today` — past session slots are ignored to avoid spurious warnings
 * when viewing historical data. Pass `today` as an ISO date string (YYYY-MM-DD).
 */
export function findConflict(
  date: ISODate,
  start: Hour,
  end: Hour,
  events: CalendarEvent[],
  ignoreId?: string,
  today?: ISODate,
  members?: Member[],
): Conflict | null {
  // Blocks/overrides first — they take visual priority
  for (const ev of events) {
    if (ev.kind === 'session') continue;
    if (ev.id === ignoreId) continue;
    if (ev.date !== date) continue;
    if (start < ev.end && end > ev.start) {
      return {
        conflictingEventId: ev.id,
        conflictingTitle: ev.title ?? ev.kind,
        conflictingKind: ev.kind as 'block' | 'override',
      };
    }
  }
  // Sessions — only checked when today is provided (opt-in, used during drag)
  if (!today) return null;
  for (const ev of events) {
    if (ev.kind !== 'session') continue;
    if (ev.id === ignoreId) continue;
    if (ev.date !== date) continue;
    if (ev.date < today) continue;
    if (start < ev.end && end > ev.start) {
      const memberName = ev.memberId
        ? members?.find((m) => m.id === ev.memberId)?.name
        : undefined;
      return {
        conflictingEventId: ev.id,
        conflictingTitle: ev.title ?? memberName ?? 'another member',
        conflictingKind: 'session',
      };
    }
  }
  return null;
}

// ---------- Overlap layout ----------

export type EventLayout = {
  event: CalendarEvent;
  /** Zero-based column index within the day column. */
  colIdx: number;
  /** Total number of concurrent columns at this event's peak overlap. */
  numCols: number;
};

/**
 * Assigns column positions to a set of events for a single day.
 *
 * Blocks and overrides always get full width (colIdx=0, numCols=1).
 * Sessions are laid out using a greedy algorithm: each session is placed in
 * the leftmost column that doesn't overlap with any event already there.
 * numCols reflects the maximum concurrent column count across all events
 * that overlap with a given session, so adjacent non-overlapping events
 * can each use more width.
 */
export function layoutDayEvents(events: CalendarEvent[]): EventLayout[] {
  const nonSessions = events.filter((ev) => ev.kind !== 'session');
  const sessions = events.filter((ev) => ev.kind === 'session');

  // Sort by start time, then longer events first for stable column assignment
  const sorted = [...sessions].sort(
    (a, b) => a.start !== b.start ? a.start - b.start : (b.end - b.start) - (a.end - a.start),
  );

  // Greedy column assignment
  const columns: CalendarEvent[][] = [];
  const colMap = new Map<string, number>();

  for (const ev of sorted) {
    let placed = false;
    for (let col = 0; col < columns.length; col++) {
      const overlaps = columns[col].some((c) => c.start < ev.end && c.end > ev.start);
      if (!overlaps) {
        columns[col].push(ev);
        colMap.set(ev.id, col);
        placed = true;
        break;
      }
    }
    if (!placed) {
      columns.push([ev]);
      colMap.set(ev.id, columns.length - 1);
    }
  }

  const sessionLayouts: EventLayout[] = sorted.map((ev) => {
    const colIdx = colMap.get(ev.id) ?? 0;
    const peakCol = sorted
      .filter((other) => other.start < ev.end && other.end > ev.start)
      .reduce((max, other) => Math.max(max, colMap.get(other.id) ?? 0), 0);
    return { event: ev, colIdx, numCols: peakCol + 1 };
  });

  return [
    ...nonSessions.map((ev) => ({ event: ev, colIdx: 0, numCols: 1 })),
    ...sessionLayouts,
  ];
}

// ---------- Week grid helpers ----------

/** Returns an array of 7 ISO dates for the week containing `cursor`. */
export function weekDates(cursor: ISODate, weekStartsOn: 0 | 1 = 1): ISODate[] {
  const start = mondayOf(cursor, weekStartsOn);
  return Array.from({ length: 7 }, (_, i) => addDays(start, i));
}

/**
 * Returns a 6×7 grid of ISO dates for the month view containing `cursor`.
 * Pads with days from adjacent months.
 */
export function monthGridDates(cursor: ISODate, weekStartsOn: 0 | 1 = 1): ISODate[] {
  const first = firstOfMonth(cursor);
  const gridStart = formatISODate(
    startOfWeek(parseISO(first), { weekStartsOn }),
  );
  return Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
}

/** Returns the ISO month string ("2026-05") for a given date. */
export function isoMonth(date: ISODate): string {
  return date.slice(0, 7);
}

// ---------- Event positioning (week view) ----------

/** Converts a decimal Hour to a pixel top offset within the time grid. */
export function hourToPixels(hour: Hour, baseHour: Hour, slotHeight: number): number {
  return (hour - baseHour) * slotHeight;
}

/** Converts duration in hours to pixels. */
export function durationToPixels(duration: Hour, slotHeight: number): number {
  return duration * slotHeight;
}

// Complement of `ranges` within [firstHour, lastHour]: the gaps a hatch should cover.
export function unavailableRanges(
  ranges: ReadonlyArray<{ start: Hour; end: Hour }>,
  firstHour: Hour,
  lastHour: Hour
): Array<{ start: Hour; end: Hour }> {
  const sorted = [...ranges].sort((a, b) => a.start - b.start);
  const gaps: Array<{ start: Hour; end: Hour }> = [];
  let cursor = firstHour;
  for (const range of sorted) {
    if (range.start > cursor) gaps.push({ start: cursor, end: Math.min(range.start, lastHour) });
    cursor = Math.max(cursor, range.end);
    if (cursor >= lastHour) break;
  }
  if (cursor < lastHour) gaps.push({ start: cursor, end: lastHour });
  return gaps;
}
